import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { AdButlerType } from "@store/_typings/adbutler";
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    placementId = "placementId",
    width = "width",
    height = "height",
    variant = "variant",
    clickMacro = "clickMacro",
    extraData = "extraData",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.placementId]: number;
        [fields.width]: number;
        [fields.height]: number;
        [fields.variant]: number;
        [fields.clickMacro]: string;
        [fields.extraData]: string;
    };
}

const mapStateToProps = (state: StoreApplicationState) => {
    const settings = getStoreSettingsCollection(state);

    return {
        adButlerEnabled: settings.poolcorpSettings.adButlerEnabled,
        adButlerId: settings.poolcorpSettings.adButlerId,
    };
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps>;

export interface AdButlerStyles {
    wrapper?: InjectableCss;
}

export const adButlerStyles: AdButlerStyles = {
    wrapper: {
        css: css`
            display: inline-block;
        `,
    },
};

const styles = adButlerStyles;

const AdButler = ({ fields, adButlerEnabled, adButlerId }: Props) => {
    if (!adButlerEnabled) {
        return null;
    }

    const { placementId, width, height, variant, extraData, clickMacro} = fields;
    const adInstanceIdRef = useRef(`placement_${placementId}_${variant}`);

    useEffect(() => {
        const loadAdButlerScript = () => {
            if (document.querySelector('script[src="https://servedbyadbutler.com/app.js"]')) {
                return;
            }

            const script = document.createElement('script');
            script.async = true;
            script.type = 'text/javascript';
            script.src = 'https://servedbyadbutler.com/app.js';
            document.body.append(script);
        };

        const displayAd = () => {
            window.AdButler = window.AdButler || ({} as AdButlerType);
            window.AdButler.ads = window.AdButler.ads || [];
            const abkw = window.abkw || '';
            window.AdButler.ads.push({
                handler (opt: any) {
                    window.AdButler!.register(
                        adButlerId,
                        placementId,
                        [width, height],
                        adInstanceIdRef.current,
                        opt
                    );
                },
                opt: {
                    place: variant,
                    keywords: abkw,
                    extraData: extraData ?? '',
                    domain: 'servedbyadbutler.com',
                    click: clickMacro ?? 'CLICK_MACRO_PLACEHOLDER',
                },
            });
        };

        loadAdButlerScript();
        setTimeout(displayAd, 0);
    }, []);

    return (
        <StyledWrapper {...styles.wrapper}>
            <div id={adInstanceIdRef.current}></div>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(AdButler),
    definition: {
        group: "Common",
        displayName: "AdButler",
        fieldDefinitions: [
            {
                name: fields.placementId,
                displayName: "Zone ID",
                editorTemplate: "IntegerField",
                defaultValue: null,
                fieldType: "General",
                sortOrder: 0,
                isRequired: true,
                tooltip: "The ID, identifiable in the url in ad butler. (For example zoneID=757522)",
            },
            {
                name: fields.width,
                displayName: "Image width",
                editorTemplate: "IntegerField",
                defaultValue: null,
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.height,
                displayName: "Image height",
                editorTemplate: "IntegerField",
                defaultValue: null,
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
            {
                name: fields.variant,
                displayName: "Variant",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                sortOrder: 3,
                isRequired: true,
                tooltip: "Variant value must be different for Ad Butler components using the same zone ID. This applies only if an ad have variants.",
            },
            {
                name: fields.clickMacro,
                displayName: "Click Macro",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.extraData,
                displayName: "Extra Data",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 5,
            },
        ],
    },
};

export default widgetModule;
